import { resolveCellType } from './tableLayout';

/**
 * Lecture interactive d'un tableau du site : tri, recherche, filtre par
 * colonne, groupes repliables. Module pur : il ne rend rien, il dit quelles
 * lignes montrer et dans quel ordre, par leurs indices **d'origine** — les
 * chemins d'édition, les clés de fusion et les styles de ligne restent ceux
 * de la donnée.
 *
 * Un tableau variable a une structure (titres, sous-totaux, totaux) qu'un tri
 * naïf mettrait en pièces. Chaque ligne a donc un rôle :
 * - `header` : ouvre un groupe (zone, poste, « MASSE SALARIALE ») ;
 * - `pinned` : reste à sa place dans son groupe (sous-total, ligne vide…) ;
 * - `total` : total du tableau (TOTAL HT, TVA, TTC), épinglé lui aussi ;
 * - `item` : ligne de données, la seule qui se trie et se filtre.
 * Les totaux qui finissent le tableau, et les lignes vides qui les précèdent,
 * n'ont pas de groupe : ils restent toujours visibles. Le sous-total du
 * dernier groupe, lui, reste dans son groupe.
 */

const SPACES = /[\s\u00a0\u202f]/g;
const DIGIT = /\d/;
const NUMBER_SHAPE = /^[-+]?\d[\d.,]*$/;
const URL = /^(https?:)?\/\//i;

/**
 * Séparateurs d'un nombre sans espaces : le dernier signe, s'il y en a deux
 * sortes, est la décimale (« 1.234,56 », « 1,234.56 »). Avec une seule sorte,
 * répétée, ce sont des milliers ; seule, la virgule est décimale (à la
 * française), le point aussi sauf devant trois chiffres pile (« 1.200 »).
 */
function toDecimal(raw) {
  const decimal = raw.lastIndexOf(',') > raw.lastIndexOf('.') ? ',' : '.';
  const other = decimal === ',' ? '.' : ',';
  if (raw.includes(other)) return raw.split(other).join('').replace(decimal, '.');

  const parts = raw.split(decimal);
  if (parts.length === 1) return raw;
  if (parts.length > 2) return parts.join('');
  const [integer, fraction] = parts;
  if (decimal === '.' && fraction.length === 3 && !/^[-+]?0$/.test(integer)) return parts.join('');
  return `${integer}.${fraction}`;
}

/**
 * Montant ou quantité (« 1 234,56 € », « 12,00 », « 5 % », « 4 575 h »,
 * « 300 €/mois »), sinon null. La devise qui précède et l'unité qui suit le
 * nombre sont ignorées.
 */
export function parseCellNumber(text) {
  const compact = String(text ?? '')
    .replace(SPACES, '')
    .replace(/^[€$£]/, '')
    .replace(/\D+$/, '');
  return NUMBER_SHAPE.test(compact) ? Number(toDecimal(compact)) : null;
}

const collator = new Intl.Collator('fr', { numeric: true, sensitivity: 'base' });

/**
 * Clés de tri d'une colonne, décidées sur toutes ses valeurs à la fois et non
 * deux à deux (une colonne mêlée trierait alors selon les voisins) : une
 * colonne dont chaque case chiffrée est un nombre se trie par valeur, ses
 * cases sans chiffre (« Inclus », « — ») en dernier ; sinon par texte, les
 * nombres d'un libellé rangés par valeur (« Poste 2 » avant « Poste 10 »).
 */
function columnSortKeys(values) {
  const numeric = values.some((value) => DIGIT.test(value))
    && values.every((value) => !DIGIT.test(value) || parseCellNumber(value) != null);
  return values.map((value) => {
    if (!value) return null;
    return numeric ? parseCellNumber(value) : value;
  });
}

/** Ordre de deux clés ; une clé nulle finit la liste, quel que soit le sens. */
function compareSortKeys(a, b, sign = 1) {
  if (a == null || b == null) return (a == null) - (b == null);
  return sign * (typeof a === 'number' ? a - b : collator.compare(a, b));
}

/**
 * Une colonne se trie si elle est de texte et qu'un de ses items porte une
 * valeur qui n'est pas une adresse d'image : une colonne de photos peut
 * arriver en texte (URL, ou vide quand le fichier manque) et la trier n'a pas
 * de sens.
 */
export function isSortableColumn(column, columnIndex, texts, roles) {
  if (resolveCellType(null, column) !== 'text') return false;
  return roles.some((role, index) => {
    const value = (texts[index] || [])[columnIndex];
    return role === 'item' && value && !URL.test(value);
  });
}

const normalize = (text) => String(text ?? '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase();

/**
 * Groupes du tableau : `{ header, rows }` où `rows` suit l'ordre d'origine
 * (items et épinglés mêlés), plus la queue des totaux.
 */
export function segmentRows(texts, roles) {
  const isTailRow = (index) => roles[index] === 'total'
    || (roles[index] === 'pinned' && (texts[index] || []).every((text) => !text));
  let tailStart = roles.length;
  while (tailStart > 0 && isTailRow(tailStart - 1)) tailStart -= 1;

  const groups = [];
  let current = { header: null, rows: [] };
  for (let index = 0; index < tailStart; index += 1) {
    if (roles[index] === 'header') {
      if (current.header != null || current.rows.length) groups.push(current);
      current = { header: index, rows: [] };
    } else {
      current.rows.push(index);
    }
  }
  if (current.header != null || current.rows.length) groups.push(current);

  const tail = [];
  for (let index = tailStart; index < roles.length; index += 1) tail.push(index);
  return { groups, tail };
}

/**
 * Lignes à montrer, dans l'ordre, par indices d'origine.
 *
 * @param {string[][]} texts   texte nu de chaque case, ligne par ligne
 * @param {string[]} roles     rôle de chaque ligne (cf. en-tête du module)
 * @param {object} options
 * @param {{ column: number, direction: 'asc'|'desc' }|null} options.sort
 * @param {string} [options.query]        recherche sur toute la ligne
 * @param {{ column: number, value: string }|null} [options.filter]
 * @param {Set<number>} [options.collapsed]  indices des titres repliés
 */
export function visibleRowIndexes(texts, roles, {
  sort = null, query = '', filter = null, group = null, collapsed = new Set(),
} = {}) {
  const { groups, tail } = segmentRows(texts, roles);
  const needle = normalize(query.trim());
  const filtering = !!needle || (filter && filter.value);
  const titleOf = (header) => groupTitle(texts, header);

  const matches = (index) => {
    const cells = texts[index] || [];
    if (filter?.value && cells[filter.column] !== filter.value) return false;
    return !needle || cells.some((text) => normalize(text).includes(needle));
  };

  const sortKeys = new Map();
  if (sort) {
    const items = roles.flatMap((role, index) => (role === 'item' ? [index] : []));
    const keys = columnSortKeys(items.map((index) => (texts[index] || [])[sort.column] ?? ''));
    items.forEach((index, position) => sortKeys.set(index, keys[position]));
  }

  const sortItems = (indexes) => {
    if (!sort) return indexes;
    const sign = sort.direction === 'desc' ? -1 : 1;
    return [...indexes].sort((a, b) => compareSortKeys(sortKeys.get(a), sortKeys.get(b), sign) || a - b);
  };

  const visible = [];
  groups.forEach(({ header, rows }) => {
    if (group && (header == null || titleOf(header) !== group)) return;
    const headerMatches = header != null && !!needle && matches(header) && !filter?.value;
    const items = rows.filter((index) => roles[index] === 'item');
    const kept = filtering && !headerMatches ? items.filter(matches) : items;
    if (filtering && kept.length === 0 && !headerMatches) return;

    if (header != null) visible.push(header);
    if (header != null && collapsed.has(header)) return;

    // Les items se trient entre eux, sur les places qu'ils occupaient : un
    // sous-total reste sous ses items.
    const sorted = sortItems(kept);
    const keptSet = new Set(kept);
    let next = 0;
    rows.forEach((index) => {
      if (roles[index] !== 'item') {
        visible.push(index);
      } else if (keptSet.has(index)) {
        visible.push(sorted[next]);
        next += 1;
      }
    });
  });
  return [...visible, ...tail];
}

/** Valeurs proposées par le filtre d'une colonne : celles de ses items. */
export function columnFilterValues(texts, roles, column) {
  const values = new Set();
  roles.forEach((role, index) => {
    const text = (texts[index] || [])[column];
    if (role === 'item' && text) values.add(text);
  });
  const list = [...values];
  const keys = columnSortKeys(list);
  return list
    .map((value, index) => ({ value, key: keys[index] }))
    .sort((a, b) => compareSortKeys(a.key, b.key))
    .map(({ value }) => value);
}

const HEADER_ROW_KINDS = ['zone', 'title'];
const TOTAL_ROW_KINDS = ['vat'];
const TOTAL_LABEL = /^total\b/i;

/**
 * Rôle d'une ligne. `rowKind` : son type (API ou déduit, cf. tableRowKinds) ;
 * `labelKind` : la sorte que la forme déclarée de la table donne à son
 * libellé (« MASSE SALARIALE » : heading, « TOTAL HT » : total). Une ligne
 * `fixed` est une ligne de données au libellé fixe (« Taux horaire ») : elle se
 * cherche comme les autres, sauf un total (« Total forfaitaire »), qui est
 * celui de son groupe.
 */
export function rowRole({ rowKind = null, labelKind = null, texts = [] }) {
  if (HEADER_ROW_KINDS.includes(rowKind) || labelKind === 'heading') return 'header';
  if (TOTAL_ROW_KINDS.includes(rowKind) || labelKind === 'total') return 'total';
  if (rowKind || labelKind === 'subtotal') return 'pinned';
  if (labelKind === 'fixed' && TOTAL_LABEL.test(texts.find(Boolean) || '')) return 'pinned';
  if (texts.every((text) => !text)) return 'pinned';
  return 'item';
}

/** Libellé d'une ligne-titre : sa première case remplie. */
function groupTitle(texts, header) {
  return (texts[header] || []).find(Boolean) || '';
}

/** Libellés des groupes, pour le filtre d'un tableau à groupes (postes, zones). */
export function groupTitles(texts, roles) {
  const titles = new Set();
  segmentRows(texts, roles).groups.forEach(({ header }) => {
    const title = header != null && groupTitle(texts, header);
    if (title) titles.add(title);
  });
  return [...titles];
}

/**
 * Chaque groupe a-t-il les mêmes lignes (une fiche par poste : Taux, Volume,
 * Total) ? Trier les lignes d'un groupe n'y a alors pas de sens : on ne
 * propose pas de tri.
 */
export function repeatsGroupStructure(texts, roles) {
  const withHeader = segmentRows(texts, roles).groups.filter(({ header }) => header != null);
  if (withHeader.length < 2) return false;
  const labelsOf = ({ rows }) => rows
    .filter((index) => roles[index] === 'item')
    .map((index) => groupTitle(texts, index))
    .join('\u0000');
  const first = labelsOf(withHeader[0]);
  return withHeader.every((entry) => labelsOf(entry) === first);
}
